import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { TwilioRoomEvents } from './constants';

const roomSelector = (state) => state.twilio.room;
export const useRoom = () => {
  return useSelector(roomSelector);
};

const selfSelector = (state) => {
  const { room } = state.twilio;
  if (!room) {
    return;
  }
  return room.localParticipant;
};
export const useLocalParticipant = () => {
  return useSelector(selfSelector);
};

const createTrackFinder = (filter) => {
  return (participant) => {
    if (!filter.current) {
      return;
    }

    for (const [, publication] of participant.audioTracks) {
      if (publication.track && filter.current(publication.track, participant)) {
        return publication.track;
      }
    }
    for (const [, publication] of participant.videoTracks) {
      if (publication.track && filter.current(publication.track, participant)) {
        return publication.track;
      }
    }
    for (const [, publication] of participant.dataTracks) {
      if (publication.track && filter.current(publication.track, participant)) {
        return publication.track;
      }
    }
  };
};

export const useTrack = (trackFilter) => {
  const filter = useRef();
  filter.current = trackFilter;
  const trackFinder = useMemo(() => createTrackFinder(filter), [filter]);

  const trackSelector = useCallback(
    (state) => {
      const { room } = state.twilio;
      if (!room) {
        return;
      }

      for (const [, participant] of room.participants) {
        const found = trackFinder(participant);
        if (found) {
          return found;
        }
      }
      return trackFinder(room.localParticipant);
    },
    [trackFinder]
  );

  const track = useSelector(trackSelector);
  useSelector(() => track && track.isEnabled); // render component when isEnabled changes
  return track;
};

export const useTrackEnabled = (trackFilter) => {
  const filter = useRef();
  filter.current = trackFilter;
  const trackFinder = useMemo(() => createTrackFinder(filter), [filter]);

  const trackEnabledSelector = useCallback(
    (state) => {
      const { room } = state.twilio;
      if (!room) {
        return;
      }

      for (const [, participant] of room.participants) {
        const found = trackFinder(participant);
        if (found) {
          return found.isEnabled;
        }
      }
      const localTrack = trackFinder(room.localParticipant);
      return localTrack && localTrack.isEnabled;
    },
    [trackFinder]
  );

  return useSelector(trackEnabledSelector);
};

export const useLocalAudioTrack = (trackFilter) => {
  const filter = useRef();
  filter.current = trackFilter;

  const track = useSelector((state) => {
    const { room } = state.twilio;
    if (!room) {
      return [];
    }
    for (const [, publication] of room.localParticipant.audioTracks) {
      const { track } = publication;
      if (!track) {
        continue;
      } else if (!filter.current) {
        return track;
      } else if (filter.current(track)) {
        return track;
      }
    }
  });
  return track;
};

export const useLocalVideoTrack = (trackFilter) => {
  const filter = useRef();
  filter.current = trackFilter;

  const track = useSelector((state) => {
    const { room } = state.twilio;
    if (!room) {
      return [];
    }
    for (const [, publication] of room.localParticipant.videoTracks) {
      const { track } = publication;
      if (!track) {
        continue;
      } else if (!filter.current) {
        return track;
      } else if (filter.current(track)) {
        return track;
      }
    }
  });
  return track;
};

export const useLocalDataTrack = (trackFilter) => {
  const filter = useRef();
  filter.current = trackFilter;

  const track = useSelector((state) => {
    const { room } = state.twilio;
    if (!room) {
      return [];
    }
    for (const [, publication] of room.localParticipant.dataTracks) {
      const { track } = publication;
      if (!track) {
        continue;
      } else if (!filter.current) {
        return track;
      } else if (filter.current(track)) {
        return track;
      }
    }
  });
  return track;
};

export const useDataTrackMessages = (trackFilter, messageHandler) => {
  const filter = useRef();
  filter.current = trackFilter;

  const eventHandler = useRef();
  eventHandler.current = messageHandler;

  const room = useSelector((state) => state.twilio.room);

  useEffect(() => {
    if (!room) {
      return;
    }

    const listener = (data, track, participant) => {
      if (filter.current && filter.current(track, participant)) {
        eventHandler.current && eventHandler.current(data, track, participant);
      }
    };
    room.on(TwilioRoomEvents.TrackMessage, listener);
    return () => {
      room.removeListener(TwilioRoomEvents.TrackMessage, listener);
    };
  }, [room]);
};

export const useNumberOfRemoteDataTrack = (trackFilter) => {
  const filter = useRef();
  filter.current = trackFilter;

  const numberOfTracks = useSelector((state) => {
    const { room } = state.twilio;
    if (!room) {
      return 0;
    }

    let value = 0;
    for (const [, participant] of room.participants) {
      for (const [, publication] of participant.dataTracks) {
        const { track } = publication;
        if (track) {
          if (!filter.current) {
            value++;
          } else if (filter.current(track, participant)) {
            value++;
          }
        }
      }
    }
    return value;
  });

  return numberOfTracks;
};
